import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAccess, useTenantDetail } from "@/lib/hooks/use-admin";
import { useMe } from "@/lib/hooks/use-clients";
import { TenantApiRateLimitCard } from "./tenant-api-rate-limit-card";
import { TenantFeatureFlagsCard } from "./tenant-feature-flags-card";

export default function TenantDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { me, isLoading: meLoading } = useMe();
  const { tenant, isLoading: tenantLoading } = useTenantDetail(id ?? null);

  const { isLoading: accessLoading, canViewAdmin } = useAdminAccess();

  const isLoading = meLoading || accessLoading || tenantLoading;

  useEffect(() => {
    if (isLoading) return;
    if (!me || !canViewAdmin) {
      navigate("/", { replace: true });
      return;
    }
    if (!tenant) {
      navigate("/", { replace: true });
    }
  }, [isLoading, me, canViewAdmin, tenant, navigate]);

  if (isLoading || !me || !canViewAdmin || !tenant) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const isQuantfolio = tenant.isQuantfolio;

  const breadcrumbItems = isQuantfolio
    ? [
        { label: "Admin", href: "/admin" },
        { label: "Tenants", href: "/tenants" },
        { label: tenant.name },
      ]
    : [
        { label: "Admin", href: "/admin" },
        { label: "Tenants", href: "/admin/tenants" },
        { label: tenant.name },
      ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={breadcrumbItems}
        title={tenant.name}
        description="Tenant configuration and details"
      />

      <TenantFeatureFlagsCard
        tenantId={tenant.id}
        featureFlags={tenant.featureFlags}
        hidePartnerFlag={!isQuantfolio}
      />

      <TenantApiRateLimitCard
        key={`${tenant.id}:${tenant.apiRateLimit.override ?? ""}`}
        tenantId={tenant.id}
        apiRateLimit={tenant.apiRateLimit}
        canEdit={isQuantfolio}
      />
    </div>
  );
}
